import React from "react";
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

import Footer from "../../../Footer/FooterBottom";
import SubMenu from "../SubMenu";
import LessonItem from "../LessonItem";
import TreeView from "../../../Admin/Indices/TreeView";
import { useTrackList } from './useTrackList';
import FilterPopover from './FilterPopover';
import SelectedFilterBadges from './SelectedFilterBadges';

const TrackList = (props) => {
    const { t } = useTranslation('common');
    const {
        currentItem,
        setCurrentItem,
        indices,
        search,
        setSearch,
        selectedSkillNames,
        selectedTextTypeNames,
        selectedDimCompNames,
        selectedElementNames,
        activeFilterTab,
        setActiveFilterTab,
        filterSearchText,
        setFilterSearchText,
        toggleFilterItem,
        clearAllFilters,
        handleSetFavorite,
        handleFetchStudentInClassRooms,
        availableSkills,
        availableTextTypes,
        availableDimComps,
        availableElements,
        hasAvailableFilters,
        totalSelectedFilters,
        getNode,
        getPathList,
        reduxLessons,
        user,
        classRooms
    } = useTrackList(props);

    const startFrom = props.startFrom;
    let start = props.defaultIndexValue;
    if (startFrom) {
        start = startFrom;
    }
    const IndexId = currentItem ? parseInt(currentItem.id) : 0;

    const renderTreeNode = (indicesData) => {
        const treeStart = props.defaultIndexValue;
        const targetId = (currentItem && currentItem.id) ? parseInt(currentItem.id) : treeStart;
        const indexNodes = getNode(indicesData.nodes, targetId);

        if (indexNodes && indexNodes.length > 0) {
            return (
                <div className="d-flex flex-wrap justify-content-center justify-content-sm-around">
                    {indexNodes.map(item => (
                        <div className="pr-1 bookWrap mb-3" key={item.id}>
                            <Link to="#" className='w-100' onClick={() => setCurrentItem(item)}>
                                <div className="shop-item mb-0">
                                    <div className="product-thumb">
                                        {item.icon ? (
                                            <LazyLoadImage src={item.icon} alt={`${item.label}`} />
                                        ) : (
                                            <LazyLoadImage src="/assets/images/logo/130p150.png" alt={`${item.label}`} />
                                        )}
                                    </div>
                                    <div className="product-content">
                                        <h5 className="title">{item.label}</h5>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderSubLessons = (IsPublic, filterLesson, toRemove, classRoomsData, userData, indicesData) => {
        return filterLesson
            .filter(p => p.IsPublic === IsPublic)
            .sort((a, b) => (a.Sort > b.Sort ? 1 : -1))
            .map(lesson => (toRemove && toRemove.includes(lesson.Id)) ? "" : (
                <LessonItem
                    setFavorite={handleSetFavorite}
                    classRooms={classRoomsData}
                    getStudent={handleFetchStudentInClassRooms}
                    user={userData}
                    type={props.typeOfLesson}
                    key={lesson.Id}
                    IsAdmin={false}
                    setIndex={setCurrentItem}
                    lesson={lesson}
                    indices={indicesData.nodes} 
                />
            ));
    };

    const renderLessons = () => {
        let isLoaded = false;
        if (reduxLessons) {
            isLoaded = reduxLessons.isLoaded;
        }

        let toRemove = [];
        if (reduxLessons) {
            toRemove = reduxLessons.toRemove;
        }

        if (reduxLessons && reduxLessons.lessons && reduxLessons.lessons.length > 0 && currentItem) {
            const searchText = search.replace(/([ًٌٍَُِّْ])/g, "").replace(/([أإٱآ])/g, "ا");
            const filterLesson = reduxLessons.lessons.filter(v => {
                const matchesSearch = v.SearchName.includes(searchText) && v.Index.Id === currentItem.id;
                if (!matchesSearch) return false;

                const skills = v.Skills || [];
                if (selectedSkillNames && selectedSkillNames.length > 0) {
                    if (!skills.some(s => s.SkillName && selectedSkillNames.includes(s.SkillName))) return false;
                }
                if (selectedTextTypeNames && selectedTextTypeNames.length > 0) {
                    if (!skills.some(s => s.TextTypeName && selectedTextTypeNames.includes(s.TextTypeName))) return false;
                }
                if (selectedDimCompNames && selectedDimCompNames.length > 0) {
                    if (!skills.some(s => s.DimensionComponentName && selectedDimCompNames.includes(s.DimensionComponentName))) return false;
                }
                if (selectedElementNames && selectedElementNames.length > 0) {
                    if (!skills.some(s => s.ElementName && selectedElementNames.includes(s.ElementName))) return false;
                }
                return true;
            });

            if (isLoaded === false) {
                return (
                    <div className="product-list-item">
                        <div className="product-content w-100 text-center">
                            <div className="display-1 text-danger">
                                <i className="fad fa-sync-alt fa-spin"></i>
                            </div>
                            <h4 className="display-3 subtitle">
                                {t("Pages." + props.typeOfList + ".Loading")}
                            </h4>
                        </div>
                    </div>
                );
            }

            if (filterLesson.length === 0 && search) {
                return (
                    <div className="product-list-item">
                        <div className="product-content w-100 text-center">
                            <div className="display-1 text-danger">
                                <i className="fad fa-telescope"></i>
                            </div>
                            <h4 className="subtitle">
                                {t("Pages." + props.typeOfList + ".NotFound")}
                            </h4>
                            <div className="h2 text-danger">
                                "{search}"
                                {t("Pages." + props.typeOfList + ".IsNotHere")}
                            </div>
                        </div>
                    </div>
                );
            }

            if (filterLesson.length === 0) {
                if (indices && indices.nodes && currentItem) {
                    return renderTreeNode(indices);
                }
                return (
                    <div className="product-list-item">
                        <div className="product-content w-100 text-center">
                            <div className="display-1 text-danger">
                                <i className="fad fa-telescope"></i>
                            </div>
                            <h4 className="display-3 subtitle">
                                {t("Pages." + props.typeOfList + ".NotFound")}
                            </h4>
                        </div>
                    </div>
                );
            }

            return (
                <>
                    {renderTreeNode(indices)}
                    {renderSubLessons(true, filterLesson, toRemove, classRooms, user, indices)}
                    {renderSubLessons(false, filterLesson, toRemove, classRooms, user, indices)}
                </>
            );
        }

        if (!currentItem) {
            return (
                <div className="product-list-item">
                    <div className="product-content w-100 text-center">
                        <div className="display-1 text-danger">
                            <i className="fad fa-glasses"></i>
                        </div>
                        <h4 className="display-3 subtitle">
                            {t("Pages." + props.typeOfList + ".SelectIndex")}
                        </h4>
                    </div>
                </div>
            );
        }

        if (reduxLessons && reduxLessons.isLoaded === false) {
            return (
                <div className="product-list-item">
                    <div className="product-content w-100 text-center">
                        <div className="display-1 text-danger">
                            <i className="fad fa-sync-alt fa-spin"></i>
                        </div>
                        <h4 className="display-3 subtitle">
                            {t("Pages." + props.typeOfList + ".Loading")}
                        </h4>
                    </div>
                </div>
            );
        }

        if (indices && indices.nodes) {
            return renderTreeNode(indices);
        }

        return (
            <div className="product-list-item">
                <div className="product-content w-100 text-center">
                    <div className="display-1 text-danger">
                        <i className="fad fa-telescope"></i>
                    </div>
                    <h4 className="display-3 subtitle">
                        {t("Pages." + props.typeOfList + ".NotFound")}
                    </h4>
                </div>
            </div>
        );
    };

    const renderPath = () => {
        const pathList = getPathList();
        if (pathList && pathList.length > 0) {
            const length = pathList.length;
            return pathList.map((node, index) => (
                <span key={node.id}>
                    <Link onClick={() => setCurrentItem(node)} to="#">
                        {node.label}
                    </Link>
                    {(length !== index + 1) ? <i className="fas fa-arrow-left text-info m-2"></i> : ""}
                </span>
            ));
        }
        return "";
    };

    return (
        <>
            <Helmet>
                <title>{t('PageHeader.' + props.typeOfList)} {props.title ? ` - ${props.title}` : ""} | {t('alarabeyya')}</title>
                <meta property="og:title" content={t("PageHeader." + props.typeOfList) + " - " + props.title + ' | ' + t('alarabeyya')} />
                <meta property="twitter:title" content={t("PageHeader." + props.typeOfList) + " - " + props.title + ' | ' + t('alarabeyya')} />
                <meta name="description" content={t('PageHeader.' + props.typeOfList + '_desc')} />
                <meta property="og:description" content={t('PageHeader.' + props.typeOfList + '_desc')} />
                <meta property="twitter:description" content={t('PageHeader.' + props.typeOfList + '_desc')} />
            </Helmet>

            <section className="position-relative" id={`main-${props.typeOfList}-section`} style={{ minHeight: '100vh' }}>
                {props.Background}
                <div className="container padding-top padding-bottom">
                    <div className="row justify-content-center w-100" data-aos="fade-top" data-aos-easing="linear">
                        <div className="section-header mb-4">
                            {props.typeOfList === "Games" ? "" : <span className="cate custome-title"> {t('PageHeader.StudyTrack')} </span>}
                            <div className="display-4 h3 custome-title">
                                <i className={`mx-3 ${props.Icon}`}></i> {t('PageHeader.' + props.typeOfList)} {props.title ? ` - ${props.title}` : ""}
                            </div>
                        </div>
                    </div>
                    {props.typeOfList === "Games" ? "" : (
                        <div className="d-none d-lg-block" data-aos="fade-bottom" data-aos-easing="linear">
                            <SubMenu />
                        </div>
                    )}

                    <div className="row mb-30-none">
                        <div className="col-lg-4 mb-50 d-none d-lg-block">
                            {indices ? (
                                <TreeView
                                    IsAdmin={false}
                                    title={props.title}
                                    parentId={props.parentId}
                                    currentId={IndexId}
                                    data={indices.nodes}
                                    startFrom={start}
                                    type={props.typeOfTree}
                                    toRemove={indices.toRemove}
                                    setCurrentItem={setCurrentItem} 
                                />
                            ) : ""}
                        </div>

                        <div className="col-lg-8 mb-50">
                            <div className="widget widget-search">
                                <div className="widget-body">
                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                        <div className="p-1 flex-grow-1" style={{ minWidth: '200px' }}>
                                            <form className="search-form m-0">
                                                <input 
                                                    type="text" 
                                                    onChange={e => setSearch(e.target.value)} 
                                                    value={search} 
                                                    placeholder={t("Pages." + props.typeOfList + ".search")} 
                                                />
                                                <button type="button"><i className="fas fa-search"></i></button>
                                            </form>
                                        </div>
                                        {hasAvailableFilters && (
                                            <div className="p-1 flex-shrink-0">
                                                <OverlayTrigger
                                                    trigger="click"
                                                    rootClose
                                                    placement="bottom-end"
                                                    overlay={
                                                        <FilterPopover
                                                            t={t}
                                                            activeFilterTab={activeFilterTab}
                                                            setActiveFilterTab={setActiveFilterTab}
                                                            filterSearchText={filterSearchText}
                                                            setFilterSearchText={setFilterSearchText}
                                                            availableSkills={availableSkills}
                                                            availableTextTypes={availableTextTypes}
                                                            availableDimComps={availableDimComps}
                                                            availableElements={availableElements}
                                                            selectedSkillNames={selectedSkillNames}
                                                            selectedTextTypeNames={selectedTextTypeNames}
                                                            selectedDimCompNames={selectedDimCompNames}
                                                            selectedElementNames={selectedElementNames}
                                                            toggleFilterItem={toggleFilterItem}
                                                            clearAllFilters={clearAllFilters}
                                                            totalSelectedFilters={totalSelectedFilters}
                                                        />
                                                    }
                                                >
                                                    <button 
                                                        type="button" 
                                                        className="btn p-0 d-flex align-items-center justify-content-center position-relative"
                                                        style={{
                                                            width: '46px',
                                                            height: '46px',
                                                            borderRadius: '12px',
                                                            background: totalSelectedFilters > 0 ? 'linear-gradient(135deg, #92278f 0%, #6b1d69 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                            color: totalSelectedFilters > 0 ? '#ffffff' : '#92278f',
                                                            border: totalSelectedFilters > 0 ? 'none' : '1px solid rgba(146, 39, 143, 0.2)',
                                                            boxShadow: totalSelectedFilters > 0 ? '0 6px 16px rgba(146, 39, 143, 0.35)' : '0 2px 8px rgba(0,0,0,0.05)',
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={t("FilterByCurriculum") || "تصفية المعايير والمهارات"}
                                                    >
                                                        <i className="fad fa-sliders-h" style={{ fontSize: '18px' }}></i>
                                                        {totalSelectedFilters > 0 && (
                                                            <span 
                                                                className="position-absolute badge badge-pill" 
                                                                style={{
                                                                    top: '-6px',
                                                                    right: '-6px',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold',
                                                                    padding: '2px 6px',
                                                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                                    color: '#ffffff',
                                                                    borderRadius: '50%',
                                                                    minWidth: '18px',
                                                                    height: '18px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    border: '2px solid #ffffff',
                                                                    boxShadow: '0 2px 5px rgba(239, 68, 68, 0.4)'
                                                                }}
                                                            >
                                                                {totalSelectedFilters}
                                                            </span>
                                                        )}
                                                    </button>
                                                </OverlayTrigger>
                                            </div>
                                        )}
                                    </div>
                                    <div className="pt-2">
                                        {renderPath()}
                                    </div>
                                    <SelectedFilterBadges
                                        t={t}
                                        selectedSkillNames={selectedSkillNames}
                                        selectedTextTypeNames={selectedTextTypeNames}
                                        selectedDimCompNames={selectedDimCompNames}
                                        selectedElementNames={selectedElementNames}
                                        toggleFilterItem={toggleFilterItem}
                                        clearAllFilters={clearAllFilters}
                                        totalSelectedFilters={totalSelectedFilters}
                                    />
                                </div>
                            </div>
                            {renderLessons()}
                        </div>
                    </div>
                </div>
            </section>
            <div className="position-sticky">
                <Footer />
            </div>
        </>
    );
};

export default TrackList;
